import { isMissingRequiredLaunchInfo } from "@/components/drop-forge/launch/launchClaimHelpers";
import type { MintingClaim } from "@/generated/models/MintingClaim";
import type { ManifoldClaim } from "@/hooks/useManifoldClaim";

type ClaimPrimaryStatusKey =
  | "draft"
  | "publishing"
  | "checking_onchain"
  | "published"
  | "pending_initialization_missing_info"
  | "pending_initialization"
  | "live"
  | "live_needs_update"
  | "diverged";

type ClaimPrimaryStatusTone =
  | "neutral"
  | "pending"
  | "success"
  | "update"
  | "destructive";

export interface ClaimPrimaryStatus {
  key: ClaimPrimaryStatusKey;
  label: string;
  tone: ClaimPrimaryStatusTone;
  reason?: string;
}

interface ClaimArweaveSectionStatus {
  key: "not_published" | "publishing" | "published";
  label: "Not Published" | "Publishing" | "Published";
  tone: ClaimPrimaryStatusTone;
  reason?: string;
}

export function getPrimaryStatusPillClassName(
  tone: ClaimPrimaryStatusTone
): string {
  if (tone === "pending") {
    return "tw-bg-amber-500/15 tw-text-amber-300 tw-ring-amber-400/40";
  }
  if (tone === "success") {
    return "tw-bg-emerald-500/15 tw-text-emerald-300 tw-ring-emerald-400/40";
  }
  if (tone === "update") {
    return "tw-bg-orange-500/15 tw-text-orange-300 tw-ring-orange-400/40";
  }
  if (tone === "destructive") {
    return "tw-bg-rose-500/15 tw-text-rose-300 tw-ring-rose-400/40";
  }
  return "tw-bg-iron-700/30 tw-text-iron-300 tw-ring-iron-500/40";
}

function getPreInitializationClaimStatus({
  hasLocalMetadata,
  initializedOnchain,
  isCraftContext,
  isManifoldClaimFetching,
  manifoldClaim,
  missingLaunchInfo,
}: {
  hasLocalMetadata: boolean;
  initializedOnchain: boolean;
  isCraftContext: boolean;
  isManifoldClaimFetching: boolean;
  manifoldClaim?: Pick<ManifoldClaim, "instanceId" | "location"> | null;
  missingLaunchInfo: boolean;
}): ClaimPrimaryStatus | null {
  if (!hasLocalMetadata && !initializedOnchain) {
    return {
      key: "draft",
      label: "Draft",
      tone: "neutral",
      reason: "Stored in DB only. Publish to Arweave to continue",
    };
  }

  if (
    !isCraftContext &&
    isManifoldClaimFetching &&
    manifoldClaim == null &&
    hasLocalMetadata
  ) {
    return {
      key: "checking_onchain",
      label: "Checking Onchain",
      tone: "pending",
      reason: "Loading current onchain claim state",
    };
  }

  if (hasLocalMetadata && !initializedOnchain) {
    if (isCraftContext) {
      return {
        key: "published",
        label: "Published",
        tone: "success",
        reason: "Published to Arweave and ready for onchain initialization",
      };
    }

    if (!missingLaunchInfo) {
      return {
        key: "pending_initialization",
        label: "Pending Initialization",
        tone: "pending",
        reason: "Ready to initialize onchain",
      };
    }
  }

  if (!initializedOnchain && missingLaunchInfo && !isCraftContext) {
    return {
      key: "pending_initialization_missing_info",
      label: "Pending Initialization — Missing Info",
      tone: "pending",
      reason: "Not onchain yet. Required launch fields are missing",
    };
  }

  return null;
}

function getInitializedClaimStatus({
  initializedOnchain,
  hasLocalMetadata,
  chainMatchesLocal,
}: {
  initializedOnchain: boolean;
  hasLocalMetadata: boolean;
  chainMatchesLocal: boolean;
}): ClaimPrimaryStatus | null {
  if (initializedOnchain && hasLocalMetadata && !chainMatchesLocal) {
    return {
      key: "live_needs_update",
      label: "Live — Needs Update",
      tone: "update",
      reason: "Onchain metadata points to an older CID",
    };
  }

  if (initializedOnchain && hasLocalMetadata && chainMatchesLocal) {
    return {
      key: "live",
      label: "Live",
      tone: "success",
      reason: "DB, Arweave, and onchain metadata all match",
    };
  }

  return null;
}

export function getClaimPrimaryStatus({
  claim,
  manifoldClaim,
  isCraftContext = false,
  isManifoldClaimFetching = false,
}: {
  claim: MintingClaim;
  manifoldClaim?: Pick<ManifoldClaim, "instanceId" | "location"> | null;
  isCraftContext?: boolean;
  isManifoldClaimFetching?: boolean;
}): ClaimPrimaryStatus {
  const resolvedManifoldClaim = manifoldClaim ?? null;
  const initializedOnchain = manifoldClaim?.instanceId != null;
  const localMetadata = claim.metadata_location?.trim() ?? "";
  const hasLocalMetadata = localMetadata.length > 0;
  const onchainLocation = manifoldClaim?.location?.trim() ?? "";
  const chainMatchesLocal =
    onchainLocation.length > 0 && onchainLocation === localMetadata;
  const missingLaunchInfo = isMissingRequiredLaunchInfo(claim);

  if (claim.media_uploading === true) {
    return {
      key: "publishing",
      label: "Publishing",
      tone: "pending",
      reason: "Uploading media and metadata to Arweave now",
    };
  }

  const preInitializationStatus = getPreInitializationClaimStatus({
    hasLocalMetadata,
    initializedOnchain,
    isCraftContext,
    isManifoldClaimFetching,
    manifoldClaim: resolvedManifoldClaim,
    missingLaunchInfo,
  });
  if (preInitializationStatus) {
    return preInitializationStatus;
  }

  const initializedStatus = getInitializedClaimStatus({
    initializedOnchain,
    hasLocalMetadata,
    chainMatchesLocal,
  });
  if (initializedStatus) {
    return initializedStatus;
  }

  return {
    key: "diverged",
    label: "Diverged",
    tone: "destructive",
    reason: "DB, Arweave, and onchain sources conflict",
  };
}

export function getClaimArweaveSectionStatus(
  claim: MintingClaim
): ClaimArweaveSectionStatus {
  if (claim.media_uploading === true) {
    return {
      key: "publishing",
      label: "Publishing",
      tone: "pending",
      reason: "Uploading media and metadata to Arweave now",
    };
  }

  if ((claim.metadata_location?.trim() ?? "").length > 0) {
    return {
      key: "published",
      label: "Published",
      tone: "success",
      reason: "Arweave metadata CID is present",
    };
  }

  return {
    key: "not_published",
    label: "Not Published",
    tone: "neutral",
    reason: "No Arweave metadata CID yet",
  };
}
